import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Checkbox,
  FormControlLabel,
  Paper,
  Chip,
  Divider,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  Description,
  ViewModule,
  CheckCircle,
  RadioButtonUnchecked,
  Image as ImageIcon,
} from '@mui/icons-material';

interface Entry {
  id: string;
  title?: string;
  name?: string;
  note?: string;
  images?: any[];
}

interface NewFileTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateBlank: () => void;
  onCreateFromTemplate: (selectedEntries: Entry[]) => void;
  availableEntries: Entry[];
}

const NewFileTemplateDialog: React.FC<NewFileTemplateDialogProps> = ({
  open,
  onClose,
  onCreateBlank,
  onCreateFromTemplate,
  availableEntries = []
}) => {
  const [showTemplate, setShowTemplate] = useState(false);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(new Set());

  const handleClose = () => {
    setShowTemplate(false);
    setSelectedEntries(new Set());
    onClose();
  };

  const handleBlankDocument = () => {
    handleClose();
    onCreateBlank();
  };

  const handleTemplateOption = () => {
    setShowTemplate(true);
  };

  const handleBack = () => {
    setShowTemplate(false);
    setSelectedEntries(new Set());
  };

  const toggleEntrySelection = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleCreateTemplate = () => {
    const selectedEntryObjects = availableEntries.filter(entry => 
      selectedEntries.has(entry.id)
    );
    handleClose();
    onCreateFromTemplate(selectedEntryObjects);
  };

  const selectAll = () => {
    const allIds = new Set(availableEntries.map(entry => entry.id));
    setSelectedEntries(allIds);
  };

  const selectNone = () => {
    setSelectedEntries(new Set());
  };

  if (!showTemplate) {
    // Initial choice dialog
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div">
            Create New Document
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose how you want to start your document
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            {/* Blank Document Option */}
            <Box sx={{ flex: 1 }}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={handleBlankDocument}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Description sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Blank Document
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Start with an empty document
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Template Option */}
            <Box sx={{ flex: 1 }}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 3 },
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column'
                }}
                onClick={handleTemplateOption}
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <ViewModule sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    From Template
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pre-populate with selected entries
                  </Typography>
                  <Chip 
                    label={`${availableEntries.length} entries available`}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                </CardContent>
              </Card>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Template selection dialog
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5" component="div">
          Select Entries for Template
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Choose which entries to include in your new document
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button size="small" onClick={selectAll}>Select All</Button>
          <Button size="small" onClick={selectNone}>Select None</Button>
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            {selectedEntries.size} of {availableEntries.length} selected
          </Typography>
        </Box>
        
        <Divider sx={{ mb: 2 }} />
        
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', 
          gap: 2 
        }}>
          {availableEntries.map((entry) => {
            const isSelected = selectedEntries.has(entry.id);
            const entryTitle = entry.title || entry.name || 'Untitled Entry';
            const imageCount = entry.images?.length || 0;
            
            return (
              <Paper
                key={entry.id}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: isSelected ? '2px solid' : '2px solid transparent',
                  borderColor: isSelected ? '#2F7A4F' : 'transparent',
                  '&:hover': { 
                    boxShadow: 2,
                    borderColor: isSelected ? '#2F7A4F' : 'grey.300'
                  },
                  position: 'relative'
                }}
                onClick={() => toggleEntrySelection(entry.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  {isSelected ? (
                    <CheckCircle sx={{ mr: 1, mt: 0.5, color: '#2F7A4F' }} />
                  ) : (
                    <RadioButtonUnchecked sx={{ mr: 1, mt: 0.5, color: 'grey.400' }} />
                  )}
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {entryTitle}
                    </Typography>
                    {entry.note && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {entry.note}
                      </Typography>
                    )}
                    {imageCount > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ImageIcon sx={{ fontSize: 16, mr: 0.5, color: 'grey.500' }} />
                        <Typography variant="caption" color="text.secondary">
                          {imageCount} image{imageCount !== 1 ? 's' : ''}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>

        {availableEntries.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No entries available. Create some entries first to use templates.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleBack}>Back</Button>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          variant="contained" 
          onClick={handleCreateTemplate}
          disabled={selectedEntries.size === 0}
          sx={{
            backgroundColor: '#2F7A4F',
            borderRadius: '20px',
            '&:hover': {
              backgroundColor: '#26693F'
            }
          }}
        >
          Create Document ({selectedEntries.size} entries)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewFileTemplateDialog;
